import { useCallback, useEffect, useRef } from 'react';

/**
 * useDebouncedCallback
 * - Returns a debounced function that runs `callback` after `delayMs` of inactivity.
 * - Automatically cancels any pending timer on unmount.
 *
 * NOTE: This is for UI-side throttling/debouncing. If you need guaranteed persistence,
 * you still want explicit saves at key moments (e.g., win/lose, Next Word).
 */
export const useDebouncedCallback = (callback, delayMs) => {
  const callbackRef = useRef(callback);
  const timerRef = useRef(null);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const debounced = useCallback(
    (...args) => {
      cancel();
      timerRef.current = setTimeout(() => {
        callbackRef.current?.(...args);
      }, delayMs);
    },
    [cancel, delayMs]
  );

  useEffect(() => cancel, [cancel]);

  return { debounced, cancel };
};

